import { useState } from 'react';
import { apiJson } from '../api/client.js';
import { FormAnswerFields } from '../forms/FormAnswerFields.jsx';
import { FaqMediaBlock } from './FaqMediaBlock.jsx';
import { SecureCredentialField } from './SecureCredentialField.jsx';

/**
 * @param {{ categories: Array<any>, plan?: { features?: Record<string, boolean> }, projectId?: string | number | null }} props
 */
export function ClientFaqViewer({ categories, plan, projectId = null }) {
  const faqBase =
    projectId != null ? `/api/my/projects/${projectId}/faq` : '/api/my/faq';
  const fileOpts = projectId != null ? { projectId: Number(projectId) } : {};
  const [openItem, setOpenItem] = useState(null);
  const [formBusy, setFormBusy] = useState(null);
  const [formMsg, setFormMsg] = useState('');

  if (!categories?.length) {
    return <p className="muted">Your agency has not published any FAQ content yet.</p>;
  }

  const submitForm = async (block, formState) => {
    setFormBusy(block.id);
    setFormMsg('');
    try {
      const result = await apiJson(`${faqBase}/blocks/${block.id}/submit`, {
        method: 'POST',
        body: { payload: formState },
      });
      setFormMsg(result.message || 'Sent — thank you!');
    } catch (e) {
      setFormMsg(e.message);
    } finally {
      setFormBusy(null);
    }
  };

  return (
    <div className="faq-viewer">
      {categories.map((cat) => (
        <section key={cat.id} className="faq-category">
          <h2 className="faq-category-title">{cat.title}</h2>
          {cat.items?.map((item) => (
            <article key={item.id} className="faq-item card-panel">
              <button
                type="button"
                className="faq-item-toggle"
                onClick={() => setOpenItem(openItem === item.id ? null : item.id)}
                aria-expanded={openItem === item.id}
              >
                <strong>{item.title}</strong>
                {item.summary ? <span className="muted small"> — {item.summary}</span> : null}
              </button>
              {openItem === item.id ? (
                <div className="faq-item-body">
                  {item.blocks?.map((block) => (
                    <FaqBlock
                      key={block.id}
                      block={block}
                      plan={plan}
                      faqBase={faqBase}
                      fileOpts={fileOpts}
                      formBusy={formBusy}
                      onSubmitForm={submitForm}
                    />
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </section>
      ))}
      {formMsg ? <p className="muted small">{formMsg}</p> : null}
    </div>
  );
}

function FaqBlock({ block, plan, faqBase, fileOpts, formBusy, onSubmitForm }) {
  const revealPath = `${faqBase}/blocks/${block.id}/reveal-credential`;
  switch (block.type) {
    case 'text':
      return <p className="faq-text">{block.content?.body}</p>;
    case 'link':
      return (
        <p>
          <a href={block.content?.url} target="_blank" rel="noreferrer">
            {block.content?.label || block.content?.url}
          </a>
        </p>
      );
    case 'image':
      if (block.approval_status === 'pending') {
        return <p className="muted small">This image is awaiting approval from your agency.</p>;
      }
      if (block.approval_status === 'rejected') {
        return (
          <p className="muted small">
            This image was not approved{block.approval_note ? `: ${block.approval_note}` : '.'}
          </p>
        );
      }
      return block.has_file || block.external_url || block.content?.url ? (
        <FaqMediaBlock block={block} variant="full" {...fileOpts} />
      ) : null;
    case 'file':
      if (block.approval_status === 'pending') {
        return <p className="muted small">This file is awaiting approval from your agency.</p>;
      }
      if (block.approval_status === 'rejected') {
        return (
          <p className="muted small">
            This file was not approved{block.approval_note ? `: ${block.approval_note}` : '.'}
          </p>
        );
      }
      return block.has_file ? <FaqMediaBlock block={block} variant="card" {...fileOpts} /> : null;
    case 'credential':
      return plan?.features?.faq_credentials !== false ? (
        <div className="faq-credentials card-panel flat">
          {block.credential?.label ? <h4>{block.credential.label}</h4> : null}
          <SecureCredentialField
            blockId={block.id}
            field="username"
            label="Username"
            masked={block.credential?.username}
            revealPath={revealPath}
          />
          <SecureCredentialField
            blockId={block.id}
            field="password"
            label="Password"
            masked={block.credential?.password}
            revealPath={revealPath}
          />
        </div>
      ) : null;
    case 'form':
      return <FaqFormBlock block={block} busy={formBusy === block.id} onSubmit={onSubmitForm} />;
    default:
      return null;
  }
}

function FaqFormBlock({ block, busy, onSubmit }) {
  const fields = block.content?.fields ?? [];
  const [state, setState] = useState(() => Object.fromEntries(fields.map((f) => [f.key, ''])));

  if (!fields.length) {
    return <p className="muted small">Form has no fields configured.</p>;
  }

  return (
    <form
      className="faq-form stack"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(block, state);
      }}
    >
      {block.content?.title ? <h4>{block.content.title}</h4> : null}
      {block.content?.description ? <p className="muted small">{block.content.description}</p> : null}
      <FormAnswerFields
        fields={fields}
        values={state}
        disabled={busy}
        onChange={(key, value) => setState((s) => ({ ...s, [key]: value }))}
      />
      <button type="submit" className="btn-primary" disabled={busy}>
        {busy ? 'Sending…' : 'Send to agency'}
      </button>
    </form>
  );
}
